import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Column, ColumnGroup, CustomButtonDefinition } from './types';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../../components/shared/confirmation-dialog/confirmation-dialog.component';
import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { DatePipe, formatDate } from '@angular/common';

type DataForEdit = {
  [key: string]: any;
  EFB_OnBlockFuel?: string | null | undefined;
  EFB_FuelUpliftLtr?: string | null | undefined;
  EFB_OffBlockFuel?: string | null | undefined;
  FSD_FuelUpliftLtr?: string | null;
  FSD_Density?: string | null;
};

@Component({
  selector: 'fca-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  providers: [DatePipe],
})
export class DataTable implements OnChanges {
  @ViewChild('thead', { static: false }) thead!: ElementRef;
  private _isFixed = false;
  @Input()
  get isFixed(): boolean {
    return this._isFixed;
  }
  set isFixed(val: boolean) {
    if (this._isFixed !== val) {
      this._isFixed = val;
    }
  }
  @Input() dataDefinition: ColumnGroup<any>[] = [];
  private _data: any[] = [];
  @Input()
  get data(): any[] {
    return this._data;
  }
  set data(val: any[]) {
    if (this._data !== val) {
      this._data = val;
      this.updateFlags();
      this.loadPageData();
      this.buildRowClasses();
    }
  }
  @Input() isLoading = false;
  private _highlightFunc: (item: any) => boolean = () => false;
  @Input()
  get highlightFunc(): (item: any) => boolean {
    return this._highlightFunc;
  }
  set highlightFunc(val: (item: any) => boolean) {
    this._highlightFunc = val ?? (() => false);
    this.buildRowClasses();
  }
  private _rowClassFunc: (item: any) => string = () => '';
  @Input()
  get rowClassFunc(): (item: any) => string {
    return this._rowClassFunc;
  }
  set rowClassFunc(val: (item: any) => string) {
    this._rowClassFunc = val ?? (() => '');
    this.buildRowClasses();
  }
  @Input() onItemUpdate: (item: any) => void = () => {};
  @Input() onColumnLockChanged: (column: Column<any>) => void = () => {};
  private _customButtons: CustomButtonDefinition<any>[] = [];
  @Input()
  get customButtons(): CustomButtonDefinition<any>[] {
    return this._customButtons;
  }
  set customButtons(val: CustomButtonDefinition<any>[]) {
    if (this._customButtons !== val) {
      this._customButtons = val ?? [];
      this.updateFlags();
    }
  }
  @Input() editModeType: 'single' | 'multiple' = 'single';
  @Input() sapExportMode = false;
  @Input() alternateRows = false;
  @Output() editMode = new EventEmitter<boolean>();
  public isEditMode = false;
  private _pageSize = 50;
  private editedRowIndex: number | null = null;
  @Input()
  get pageSize(): number {
    return this._pageSize;
  }
  set pageSize(val: number) {
    if (this._pageSize !== val) {
      this._pageSize = val;
      this.updateFlags();
      this.loadPageData();
    }
  }
  @Input() expandableRows = false;
  @Input() expandRowOnClick = true;
  @Input() rowDetailsTemplate?: TemplateRef<any>;
  @Input() canExpandRow: (item: any, rowIndex: number) => boolean = () => true;
  @Input() getRowDetails: (item: any, rowIndex: number) => any = (item) =>
    item?.details ?? null;

  expandedRows: { [key: number]: boolean } = {};

  pageData: any[] = [];
  buttonsColumnVisible = true;
  allowEditing = false;
  allowDelete = false;
  maxLength = 60;

  buttonRowSpans: { index: number; span: number }[] = [];
  editingRowSpans: { index: number; span: number }[] = [];

  constructor(private dialog: MatDialog, private datePipe: DatePipe) {}

  onHeaderClick(column: Column<any>) {
    if (column.clickable && column.onHeaderClick) {
      column.onHeaderClick();
    }
  }

  errorMessage(column: any, rowIndex: number): string | null {
    const value = this.dataForEdit[rowIndex][column.fieldName];
    return column.validator ? column.validator(value) : null;
  }

  pageIndex = 0;
  rowspanData: number[][] = [];

  editingRowIndex = -1;
  dataForEdit: { [index: number]: DataForEdit } = {};
  editableRows: number[] = [];

  formatDateValue(
    date: string | Date,
    format: string,
    toUpperCase: boolean = false
  ): string {
    if (!date) {
      return '';
    }

    try {
      let dateObj: Date;
      if (typeof date === 'string') {
        const standardizedDateString = date.replace(
          /(\d{2})\.(\d{2})\.(\d{4})/,
          '$3-$2-$1'
        );
        dateObj = new Date(standardizedDateString);
      } else {
        dateObj = date;
      }

      const formattedDate = formatDate(dateObj, format, 'en-US');
      return toUpperCase
        ? formattedDate.toUpperCase().padStart(7, '0')
        : formattedDate;
    } catch (error) {
      return '';
    }
  }

  updateFlags() {
    this.allowEditing = this.dataDefinition.some((group) =>
      group.columns.some((col) => col.editable)
    );
    this.allowDelete = this.dataDefinition.some((group) =>
      group.columns.some((col) => col.deletable)
    );
    const hasButtons = (this.customButtons?.length ?? 0) > 0;
    this.buttonsColumnVisible = this.allowEditing || this.allowDelete || hasButtons;
  }

  getCustomButtonsTitle(): string {
    const buttonWithTitle = this.customButtons.find((button) => button.title);
    return buttonWithTitle?.title || '';
  }

  loadPageData() {
    if (!this.data) return;

    this.resetExpandedRows();

    if (this.data.length <= this.pageSize && this.pageIndex > 0) {
      this.pageIndex = 0;
    }

    const start = this.pageIndex * this.pageSize;
    const end = Math.min(
      (this.pageIndex + 1) * this.pageSize,
      this.data.length
    );

    this.sapExportMode
      ? (this.pageData = this.data)
      : (this.pageData = this.data.slice(start, end));
    this.calculateRowSpan();
    this.buildRowClasses();
  }

  resetExpandedRows() {
    this.expandedRows = {};
  }

  calculateRowSpan() {
    this.rowspanData = this.dataDefinition.map(() => []);
    for (let g = 0; g < this.dataDefinition.length; g++) {
      const group = this.dataDefinition[g];
      this.rowspanData[g] = this.pageData.map(() => 1);

      if (group.mergeByField) {
        const field = group.mergeByField;
        let currentTopRow = 0;

        for (let i = 0; i < this.pageData.length; i++) {
          if (i > 0 && this.pageData[i][field] !== this.pageData[i - 1][field]) {
            currentTopRow = i;
          }
          if (currentTopRow !== i) {
            this.rowspanData[g][currentTopRow]++;
            this.rowspanData[g][i] = 0;
          }
        }
      } else if (
        group.name === 'FLIGHT DATA' ||
        group.name === 'EFB FUEL DATA' ||
        group.name === 'FUEL SUPPLIER DATA'
      ) {
        let currentTopRow = 0;

        for (let i = 0; i < this.pageData.length; i++) {
          if (
            i > 0 &&
            (this.pageData[i].FD_LegNumber !==
              this.pageData[i - 1].FD_LegNumber ||
              this.pageData[i].legNumber !== this.pageData[i - 1].legNumber)
          ) {
            currentTopRow = i;
          }

          if (currentTopRow !== i) {
            this.rowspanData[g][currentTopRow]++;
            this.rowspanData[g][i] = 0;
          }
        }
      } else if (
        group.name === 'FUEL_SUPPLIERS' ||
        group.name === 'FUEL_SUPPLIERS_OTHER' ||
        group.name === 'FUEL_SUPPLIERS_CONTACTS'
      ) {
        let currentTopRow = 0;

        for (let i = 0; i < this.pageData.length; i++) {
          if (i > 0 && this.pageData[i].name !== this.pageData[i - 1].name) {
            currentTopRow = i;
          }

          if (currentTopRow !== i) {
            this.rowspanData[g][currentTopRow]++;
            this.rowspanData[g][i] = 0;
          }
        }
      } else {
        this.rowspanData[g] = this.pageData.map(() => 1);
      }
    }
    this.calculateButtonRowSpans();
  }

  calculateButtonRowSpans() {
    const hasMergeByField = this.dataDefinition.some(g => g.mergeByField);

    if (hasMergeByField) {
      this.buttonRowSpans = this.pageData.map((_, index) => ({ index, span: 1 }));
      this.editingRowSpans = [...this.buttonRowSpans];
      return;
    }

    const groupIndices = this.rowspanData[0]
      .map((span, index) => (span > 0 ? index : -1))
      .filter((index) => index !== -1);

    this.buttonRowSpans = groupIndices.map((index) => ({
      index,
      span: this.rowspanData[0][index],
    }));

    this.editingRowSpans = [...this.buttonRowSpans];
  }

  getButtonRowSpan(rowIndex: number): number {
    const buttonRow = this.buttonRowSpans.find((row) => row.index === rowIndex);
    return buttonRow ? buttonRow.span : 1;
  }

  getButtonRowIndex(rowIndex: number): boolean {
    return this.buttonRowSpans.some((row) => row.index === rowIndex);
  }

  public rowClasses: string[] = [];

  private buildRowClasses(): void {
    this.rowClasses = [];
    let lastRowWasAlternate = false;

    for (let i = 0; i < this.pageData.length; i++) {
      let alternateRow = false;
      if (this.alternateRows) {
        for (
          let groupIndex = 0;
          groupIndex < this.dataDefinition.length;
          groupIndex++
        ) {
          if (this.rowspanData[groupIndex][i] > 0) {
            alternateRow = !lastRowWasAlternate;
          } else {
            alternateRow = lastRowWasAlternate;
          }
        }
        lastRowWasAlternate = alternateRow;
      }

      const isHighlighted = this.highlightFunc(this.pageData[i]);
      const extraClass = this.rowClassFunc(this.pageData[i]);
      if (isHighlighted) {
        this.rowClasses[i] = ['highlighted', extraClass].filter(Boolean).join(' ');
      } else {
        const baseClass = alternateRow ? 'alternate-row' : '';
        this.rowClasses[i] = [baseClass, extraClass].filter(Boolean).join(' ');
      }
    }
  }

  formatSubFieldValue(item: any, fieldName: string, valueType: string, format?: string): string {
    const val = item[fieldName];
    if (val === null || val === undefined || val === '') return '—';
    if (valueType === 'date' && format) {
      return this.formatDateValue(val, format);
    }
    return String(val);
  }

  getAlignClass(column: Column<any>) {
    return 'center';
  }

  shouldDisplayGroup(groupIndex: number, rowIndex: number) {
    return this.dataDefinition[groupIndex].allowGroup
      ? this.rowspanData[groupIndex][rowIndex] > 0
      : true;
  }

  isColumnEditable(column: Column<any>, record: any): boolean {
    return column.editable
      ? column.editable === true || column.editable(record)
      : false;
  }

  isInfinityEditable(column: Column<any>, record: any): boolean {
    return (
      !this.isEditMode &&
      !this.isColumnEditable(column, record) &&
      (column.infinityEditable
        ? column.infinityEditable === true || column.infinityEditable(record)
        : false)
    );
  }

  getRowSpan(groupIndex: number, rowIndex: number) {
    return this.dataDefinition[groupIndex].allowGroup
      ? this.rowspanData[groupIndex][rowIndex]
      : 1;
  }

  handlePaginatorEvent(e: PageEvent) {
    this.pageSize = e.pageSize;
    this.pageIndex = e.pageIndex;
    this.loadPageData();
  }

  onEditClick(rowIndex: number) {
    if (this.editModeType === 'multiple') {
      const legNumber = this.pageData[rowIndex].FD_LegNumber;
      this.editableRows = this.pageData
        .map((item, index) => (item.FD_LegNumber === legNumber ? index : -1))
        .filter((index) => index !== -1);

      this.editableRows.forEach((index) => {
        this.dataForEdit[index] = { ...this.pageData[index] };
      });

      this.editingRowSpans = this.editableRows.map((index) => ({
        index,
        span: this.editableRows.length,
      }));
    } else {
      this.editableRows = [rowIndex];
      this.dataForEdit[rowIndex] = { ...this.pageData[rowIndex] };

      this.editingRowSpans = [{ index: rowIndex, span: 1 }];
    }

    this.editedRowIndex = rowIndex;
    this.editMode.emit(true);
    this.isEditMode = true;
  }

  onSaveClick() {
    if (this.editedRowIndex !== null && this.editableRows.length > 0) {
      if (this.editModeType === 'multiple') {
        const efbFields = [
          'EFB_OnBlockFuel',
          'EFB_FuelUpliftLtr',
          'EFB_OffBlockFuel',
        ];
        const fsdFields = ['FSD_FuelUpliftLtr', 'FSD_Density'];

        const hasModifiedEfbRecords = this.editableRows.some((index) =>
          efbFields.some(
            (field) =>
              this.dataForEdit[index][field] !== this.pageData[index][field]
          )
        );

        const hasModifiedFsdRecords = this.editableRows.some((index) =>
          fsdFields.some(
            (field) =>
              this.dataForEdit[index][field] !== this.pageData[index][field]
          )
        );

        if (hasModifiedEfbRecords || hasModifiedFsdRecords) {
          const isEfbEmpty =
            hasModifiedEfbRecords &&
            this.editableRows.every((index) =>
              this.checkIfSectionEmpty(efbFields, this.dataForEdit[index])
            );
          const isFsdEmpty =
            hasModifiedFsdRecords &&
            this.editableRows.every((index) =>
              this.checkIfSectionEmpty(fsdFields, this.dataForEdit[index])
            );

          if (isEfbEmpty || isFsdEmpty) {
            this.openConfirmationDialog(
              'Attention',
              'Are you sure you want to clear these values?',
              'No',
              'Yes',
              () => {
                this.clearValues();
              }
            );
            return;
          }

          const isEfbIncomplete =
            hasModifiedEfbRecords &&
            this.editableRows.some((index) =>
              this.checkIfSectionIncomplete(efbFields, this.dataForEdit[index])
            );
          const isFsdIncomplete =
            hasModifiedFsdRecords &&
            this.editableRows.some((index) =>
              this.checkIfSectionIncomplete(fsdFields, this.dataForEdit[index])
            );

          if (isEfbIncomplete || isFsdIncomplete) {
            this.openConfirmationDialog(
              'Attention',
              'Please complete all fields in this section.',
              '',
              'OK'
            );
            return;
          }

          try {
            this.editableRows.forEach((index) => {
              this.onItemUpdate(this.dataForEdit[index]);
            });
          } catch (err) {
            console.error(err);
          }

          this.editableRows.forEach((index) => {
            const pageIndex = this.pageData.findIndex(
              (item) => item.FSD_ID === this.dataForEdit[index].FSD_ID
            );
            if (pageIndex > -1) {
              this.pageData[pageIndex] = this.dataForEdit[index];
            }
          });
        }
      } else {
        try {
          this.editableRows.forEach((index) => {
            this.onItemUpdate(this.dataForEdit[index]);
          });
        } catch (err) {
          console.error(err);
        }

        this.editableRows.forEach((index) => {
          const pageIndex = this.pageData.findIndex(
            (item) => item.FSD_ID === this.dataForEdit[index].FSD_ID
          );
          if (pageIndex > -1) {
            this.pageData[pageIndex] = this.dataForEdit[index];
          }
        });
      }

      this.dataForEdit = {};
      this.editedRowIndex = null;
      this.editableRows = [];
      this.editMode.emit(false);
      this.isEditMode = false;
      this.loadPageData();
    }
  }

  checkIfSectionIncomplete(fields: string[], record: any): boolean {
    return fields.some((field) => {
      const value = record[field as keyof DataForEdit];
      return value === null || value === undefined || value === '';
    });
  }

  checkIfSectionEmpty(fields: string[], record: any): boolean {
    return fields.every((field) => {
      const value = record[field as keyof DataForEdit];
      return value === null || value === undefined || value === '';
    });
  }

  areAllFieldsEmpty(record: any, fields: string[]): boolean {
    return fields.every((field) => {
      const isEmpty =
        record[field] === null ||
        record[field] === undefined ||
        record[field] === '';
      return isEmpty;
    });
  }

  clearValues() {
    const efbFields = [
      'EFB_OnBlockFuel',
      'EFB_FuelUpliftLtr',
      'EFB_OffBlockFuel',
    ];
    const fsdFields = ['FSD_FuelUpliftLtr', 'FSD_Density'];

    this.editableRows.forEach((index) => {
      if (this.areAllFieldsEmpty(this.dataForEdit[index], efbFields)) {
        this.clearSectionFields(efbFields, index);
      }
      if (this.areAllFieldsEmpty(this.dataForEdit[index], fsdFields)) {
        this.clearSectionFields(fsdFields, index);
      }
    });

    this.editableRows.forEach((index) => {
      this.onItemUpdate(this.dataForEdit[index]);
      const pageIndex = this.pageData.findIndex(
        (item) => item.FSD_ID === this.dataForEdit[index].FSD_ID
      );
      if (pageIndex > -1) {
        this.pageData[pageIndex] = this.dataForEdit[index];
      }
    });

    this.dataForEdit = {};
    this.editableRows = [];
    this.editMode.emit(false);
    this.isEditMode = false;
    this.loadPageData();
  }

  clearSectionFields(fields: string[], rowIndex: number) {
    fields.forEach((field) => {
      this.dataForEdit[rowIndex][field as keyof DataForEdit] = null;
    });
  }

  onCancelClick() {
    this.dataForEdit = {};
    this.editableRows = [];
    this.editedRowIndex = null;
    this.editMode.emit(false);
    this.isEditMode = false;
  }

  onFieldChanged(column: any, event: any, rowIndex: number) {
    const legNumber = this.pageData[rowIndex].FD_LegNumber;
    const efbFields = [
      'EFB_OnBlockFuel',
      'EFB_FuelUpliftLtr',
      'EFB_OffBlockFuel',
    ];

    if (efbFields.includes(column.fieldName)) {
      this.editableRows.forEach((index) => {
        if (this.pageData[index].FD_LegNumber === legNumber) {
          if (!this.dataForEdit[index]) {
            this.dataForEdit[index] = {};
          }
          this.dataForEdit[index][column.fieldName] = event.target.value
            .toString()
            .replace(',', '.');
        }
      });
    } else {
      if (!this.dataForEdit[rowIndex]) {
        this.dataForEdit[rowIndex] = {};
      }
      this.dataForEdit[rowIndex][column.fieldName] = event.target.value
        .toString()
        .replace(',', '.');
    }
  }

  onCheckBoxFieldChanged(column: any, rowIndex: number, event: any) {
    if (
      (column.fullColumnEdit && !column.locked) ||
      (column.defaultChecked && !column.locked)
    ) {
      this.pageData[rowIndex][column.fieldName] = event.checked;
    } else {
      if (!this.dataForEdit[rowIndex]) {
        this.dataForEdit[rowIndex] = {};
      }
      this.dataForEdit[rowIndex][column.fieldName] = event.checked;
      this.pageData[rowIndex][column.fieldName] = event.checked;
      if (column.onInfinityChange) {
        column.onInfinityChange(this.pageData[rowIndex], event.checked);
      }
    }
  }

  isRowValid() {
    return this.editableRows.every((rowIndex) => {
      return this.dataDefinition.every((group) =>
        group.columns.every((column) => {
          const error = this.errorMessage(column, rowIndex);
          return error === null;
        })
      );
    });
  }

  columnLockChange(column: Column<any>, change: MatSlideToggleChange) {
    column.locked = !change.checked;
    this.isEditMode = change.checked;
    this.onColumnLockChanged(column);
  }

  openConfirmationDialog(
    title: string,
    message: string,
    cancelText: string,
    confirmText: string,
    onConfirm?: () => void
  ) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '350px',
      scrollStrategy: new NoopScrollStrategy(),
      data: {
        title,
        message,
        buttons: {
          cancel: cancelText,
          confirm: confirmText,
        },
      },
    });

    if (onConfirm) {
      dialogRef.afterClosed().subscribe((result) => {
        if (result === confirmText) {
          onConfirm();
        }
      });
    }
  }

  checkLength(value: string): string {
    if (value?.length >= this.maxLength) {
      return value.slice(0, this.maxLength);
    } else {
      return value;
    }
  }

  getClass(item: any, rowIndex: number) {
    const legNumber = item?.legNumber;
    const sameLegRows = this.pageData.filter(
      (row) => row.legNumber === legNumber
    );

    const isLegBeingEdited =
      this.isEditMode &&
      sameLegRows.some((row) =>
        this.editableRows.includes(this.pageData.indexOf(row))
      );

    const shouldHighlight =
      item?.flightNumber === '0000' ||
      item?.refuelingIndicator === 'Y' ||
      item?.remainingFuel === 0 ||
      this.editableRows.includes(rowIndex) ||
      isLegBeingEdited;

    return {
      highlight: shouldHighlight,
    };
  }

  getTotalColumns(): number {
    const dataColumns = this.dataDefinition.reduce(
      (sum, group) => sum + group.columns.length,
      0
    );

    return this.buttonsColumnVisible ? dataColumns + 1 : dataColumns;
  }

  isExpandableFeatureEnabled(): boolean {
    const hasGrouping = this.dataDefinition.some(
      (group) => !!group.mergeByField || !!group.allowGroup
    );

    return this.expandableRows && !!this.rowDetailsTemplate && !hasGrouping;
  }

  canExpand(item: any, rowIndex: number): boolean {
    return this.isExpandableFeatureEnabled() && this.canExpandRow(item, rowIndex);
  }

  isRowExpanded(rowIndex: number): boolean {
    return this.expandedRows[rowIndex] === true;
  }

  toggleExpandedRow(item: any, rowIndex: number, event?: Event) {
    event?.stopPropagation();

    if (!this.canExpand(item, rowIndex)) {
      return;
    }

    this.expandedRows[rowIndex] = !this.expandedRows[rowIndex];
  }

  onRowClick(item: any, rowIndex: number, event: MouseEvent) {
    if (!this.expandRowOnClick) {
      return;
    }

    this.toggleExpandedRow(item, rowIndex, event);
  }

  getRowDetailsContext(item: any, rowIndex: number) {
    return {
      $implicit: item,
      item,
      detail: this.getRowDetails(item, rowIndex),
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isFixed) {
      this.updateStickyClass();
    }
    if (changes.dataDefinition || changes.customButtons) {
      this.updateFlags();
    }
    if (changes.highlightFunc) {
      this.buildRowClasses();
    }
    if (changes.expandableRows && !this.expandableRows) {
      this.resetExpandedRows();
    }
  }

  private updateStickyClass(): void {
    if (this.isFixed) {
      this.thead?.nativeElement?.classList.add('sticky-table-header');
    } else {
      this.thead?.nativeElement?.classList.remove('sticky-table-header');
    }
  }
}
